import { Button, CircularProgress, Typography } from '@fox/atoms';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { useFormEventContext } from '../form-event-context';

export const CountdownTimer = ({ loading }: { loading: boolean }) => {
	const { extendReservation, createReservation, event } = useFormEventContext();
	const reservation = event?.reservation;

	const theme = useTheme();
	const endTime = reservation?.expiry;

	const [remainingTime, setRemainingTime] = useState(() => {
		if (!endTime) return null;
		const currentTime = new Date().getTime();
		const end = new Date(endTime).getTime();
		return end - currentTime;
	});

	const [isExpired, setIsExpired] = useState(false);
	const [isExtending, setIsExtending] = useState(false);

	useEffect(() => {
		const intervalId = setInterval(() => {
			const currentTime = new Date().getTime();
			const end = new Date(endTime).getTime();

			if (currentTime >= end) {
				setRemainingTime(0);
				setIsExpired(true);
				clearInterval(intervalId);
				return;
			}

			const remaining = end - currentTime;
			setRemainingTime(remaining);
		}, 1000);

		return () => clearInterval(intervalId);
	}, [endTime]);

	const formatTime = (milliseconds: number) => {
		const totalSeconds = Math.floor(milliseconds / 1000);
		const minutes = Math.floor(totalSeconds / 60);
		const seconds = totalSeconds % 60;
		return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
	};

	const THIRTY_SECONDS_IN_MS = 30_000;
	const SIXTY_SECONDS_IN_MS = 60_000;

	if (isExpired) {
		return (
			<BannerContainer>
				<Typography variant="body1">Your session has expired. Click to check event availability.</Typography>
				<Button
					sx={{ textTransform: 'none' }}
					size="small"
					variant="primary"
					onClick={async () => {
						setIsExpired(false);
						await createReservation();
					}}
				>
					Check Availability
				</Button>
			</BannerContainer>
		);
	}

	if (loading || !endTime || !remainingTime) {
		return (
			<BannerContainer>
				<Typography variant="body1">Checking event availability...</Typography>
			</BannerContainer>
		);
	}

	return (
		<BannerContainer>
			{isExtending ? (
				<Typography variant="body1">Extending reservation...</Typography>
			) : (
				<>
					<Typography variant="body1">Time left to complete your registration:</Typography>
					<Typography variant="body1" sx={{ color: remainingTime <= THIRTY_SECONDS_IN_MS ? theme.palette.warning.main : undefined }}>
						{formatTime(remainingTime)}
					</Typography>
				</>
			)}
			{remainingTime <= SIXTY_SECONDS_IN_MS && moment(endTime).add(5, 'minutes').isBefore(moment(reservation.maxExpiryUtcDateTime)) ? (
				<Button
					sx={{ textTransform: 'none' }}
					size="small"
					variant="primary"
					disabled={isExtending}
					onClick={async () => {
						setIsExtending(true);
						await extendReservation({ messageKey: 'event-reservation', safeExpiry: false }).then(() => setIsExtending(false));
					}}
				>
					{isExtending ? <CircularProgress size={24} /> : 'Extend Reservation'}
				</Button>
			) : null}
		</BannerContainer>
	);
};

const BannerContainer = ({ children }) => {
	const theme = useTheme();

	return (
		<div style={{ width: '100%', backgroundColor: theme.palette.grey[100], padding: '1rem' }}>
			<Typography sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, gap: '2rem' }}>{children}</Typography>
		</div>
	);
};
